import {
  createContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PackageSessionStoreRequest } from '~/types/api';
import { useOptions } from '~/hooks/useOptions';
import usePackageSessionStore from '~/hooks/bookingFlow/usePackageSessionStore';
import { InferredBookingFlowOptions } from '~/types/options';
import Alert from '~/components/General/Alert';
import useSessionId from '~/hooks/bookingFlow/useSessionId';
import { BookingFlowOverviewContextType, BookingFlowOverviewProviderProps } from '~/types/booking-flow-context';
import { applyRules } from '~/helpers/optionRules/applyRules';
import usePackageSession from '~/hooks/bookingFlow/usePackageSession';

export const BookingFlowOverviewContext = createContext<BookingFlowOverviewContextType>({
  overviewFormValue: undefined,
  updateOverviewFormValue: (
    _formValue: PackageSessionStoreRequest,
    _refetch: boolean = false,
  ) => {},
  formErrors: [],
  isFormLoading: false,
  showFormErrors: false,
  setShowFormErrors: () => {},
});

export default function BookingFlowOverviewProvider({
  children,
}: BookingFlowOverviewProviderProps) {
  // Retrieve the options passed to the embed, for example the locale, package id, etc.
  const options = useOptions() as InferredBookingFlowOptions;

  const queryClient = useQueryClient();

  // Retrieve the session ID from the URL, if it is set.
  const { sessionId, setSessionId } = useSessionId();

  // This is the initial form value, only the necessary fields that are given
  // by options when rendering the booking flow. The formValue will be expanded
  // after the first request to the backend.
  //
  // We only tell the API how many people (pax), start_date and the package.
  // The API will respond with all order lines, rooms, room types, etc.
  const [overviewFormValue, setOverviewFormValue] = useState<PackageSessionStoreRequest>({
    session_id: sessionId,
    package_id: options.packageId,
    start_date: options.date,
    pax: options.pax,
    locale: options.locale,
    office_id: options.officeId,
    department_id: options.departmentId,
  });
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [showFormErrors, setShowFormErrors] = useState(false);

  // Mutation to store the package session in the backend.
  const packageSessionStoreMutation = usePackageSessionStore();

  // Retrieve the package session from the backend, note that when the session is
  // not found, the data will be undefined.
  const {
    data: packageSessionData,
    error: packageSessionError,
    isPending: packageSessionPending,
  } = usePackageSession({
    sessionId: sessionId || '',
    locale: options.locale,
  });

  // If the database returned a package session, set the overview form value
  // to update the form.
  useEffect(() => {
    if (packageSessionData) {
      const formValue = { ...packageSessionData };

      // Pass the data through the order line rules and groups.
      const { orderLines: appliedOrderLines, errors: appliedErrors } = applyRules(
        formValue.order_lines || [],
        formValue.default_order_lines || [],
        formValue.order_line_rules || [],
        formValue.order_line_groups || [],
        formValue.pax,
      );

      // Set the errors from the order line rules and groups.
      setFormErrors(appliedErrors);

      if (!appliedErrors.length) {
        setShowFormErrors(false);
      }

      // Store the modified order lines.
      formValue.order_lines = appliedOrderLines;

      setOverviewFormValue(formValue);
    }
  }, [packageSessionData]);

  // If the session ID is not set, initiate a new session.
  useEffect(() => {
    async function initiateNewSession() {
      const res = await packageSessionStoreMutation.mutateAsync(overviewFormValue);

      if (res.session_id) {
        setSessionId(res.session_id);
      }
    }

    if (!sessionId) {
      initiateNewSession();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If in the store we get another session ID, update the session ID in the URL.
  // This is important, because it is possible that the session the user has
  // in the URL is no longer valid. The server returns a new session ID.
  useEffect(() => {
    if (
      sessionId
      && packageSessionStoreMutation.data?.session_id
      && packageSessionStoreMutation.data?.session_id !== sessionId
    ) {
      setSessionId(packageSessionStoreMutation.data.session_id);
    }
  }, [packageSessionStoreMutation.data?.session_id, sessionId, setSessionId]);

  const updateOverviewFormValue = useCallback(async (
    newFormValue: PackageSessionStoreRequest,
    refetch: boolean = false,
  ) => {
    const mergedFormValue = {
      ...overviewFormValue,
      ...newFormValue,
    };

    // Pass the data through the order line rules and groups.
    const { orderLines: appliedOrderLines, errors: appliedErrors } = applyRules(
      mergedFormValue.order_lines || [],
      mergedFormValue.default_order_lines || [],
      mergedFormValue.order_line_rules || [],
      mergedFormValue.order_line_groups || [],
      mergedFormValue.pax,
    );

    // Set the errors from the order line rules and groups.
    setFormErrors(appliedErrors);

    if (!appliedErrors.length) {
      setShowFormErrors(false);
    }

    // Store the modified order lines.
    mergedFormValue.order_lines = appliedOrderLines;

    setOverviewFormValue(mergedFormValue);

    // Store the update in the database.
    await packageSessionStoreMutation.mutateAsync(mergedFormValue);

    if (refetch) {
      queryClient.invalidateQueries({ queryKey: ['packageSession'] });
    }
  }, [overviewFormValue, packageSessionStoreMutation, queryClient]);

  const value = useMemo(() => {
    return {
      overviewFormValue,
      setOverviewFormValue,
      updateOverviewFormValue,
      formErrors,
      isFormLoading: packageSessionPending || packageSessionStoreMutation.isPending,
      showFormErrors,
      setShowFormErrors,
    };
  }, [
    overviewFormValue,
    updateOverviewFormValue,
    packageSessionPending,
    packageSessionStoreMutation.isPending,
    formErrors,
    showFormErrors,
    setShowFormErrors,
  ]);

  if (packageSessionError) {
    return (
      <Alert
        message={packageSessionError.message}
      />
    );
  }

  return (
    <BookingFlowOverviewContext.Provider value={value}>
      {children}
    </BookingFlowOverviewContext.Provider>
  );
}
